import React, { useMemo } from 'react';
import { useEyeconxStore } from '../../store/eyeconxStore';
import {
    SMS_SEGMENT_LENGTH,
    buildSmsPreviewValues,
    getSmsSegmentCount,
    replaceSmsKeywords,
} from './smsPreviewUtils';
import './MessageContentStep.css';
import './CompleteStep.css';

const formatPhoneDisplay = (value) => {
    if (value == null || value === '') {
        return '—';
    }
    const digits = String(value).replace(/\D/g, '');
    if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    if (digits.length === 11 && digits.startsWith('1')) {
        return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    return String(value);
};

const formatPatientName = (patient) => {
    const fullName = [patient.firstName, patient.lastName].filter(Boolean).join(' ').trim();
    if (fullName) {
        return fullName;
    }
    return patient.name || patient.preferredName || 'Unknown';
};

function CompleteStep({
    formData,
    additionalFields = [],
    entityLabel = 'Campaign',
    campaignType = 'sms',
}) {
    const clinicInfo = useEyeconxStore((state) => state.clinicInfo);
    const isSms = campaignType !== 'email';
    const messageContent = formData.messageContent || '';
    const selectedPatients = formData.selectedPatients || [];
    const patientCount = selectedPatients.length;
    const charCount = messageContent.length;
    const segmentCount = getSmsSegmentCount(charCount);

    const previewValues = useMemo(() => buildSmsPreviewValues(clinicInfo), [clinicInfo]);
    const previewMessage = useMemo(
        () => replaceSmsKeywords(messageContent, previewValues),
        [messageContent, previewValues]
    );

    const checks = [
        {
            key: 'name',
            label: `${entityLabel} name`,
            ok: Boolean(formData.campaignName?.trim()),
            detail: formData.campaignName?.trim() || 'Required before launch',
        },
        {
            key: 'message',
            label: isSms ? 'SMS message' : 'Email content',
            ok: isSms
                ? Boolean(messageContent.trim())
                : Boolean(formData.subjectLine?.trim() || formData.content),
            detail: isSms
                ? messageContent.trim()
                    ? `${charCount} characters · ${segmentCount} segment${segmentCount === 1 ? '' : 's'}`
                    : 'Add message content before launch'
                : formData.subjectLine?.trim() || 'Add a subject line before launch',
        },
        {
            key: 'patients',
            label: 'Recipients',
            ok: patientCount > 0,
            detail:
                patientCount > 0
                    ? `${patientCount} patient${patientCount === 1 ? '' : 's'} selected`
                    : 'Select at least one patient',
        },
    ];

    const readyCount = checks.filter((check) => check.ok).length;
    const isReady = readyCount === checks.length;
    const previewPatients = selectedPatients.slice(0, 6);
    const remainingPatients = Math.max(patientCount - previewPatients.length, 0);

    const stats = [
        {
            key: 'patients',
            label: 'Recipients',
            value: String(patientCount),
            icon: 'bi-people',
        },
        ...(isSms
            ? [
                  {
                      key: 'chars',
                      label: 'Characters',
                      value: String(charCount),
                      icon: 'bi-fonts',
                  },
                  {
                      key: 'segments',
                      label: 'SMS segments',
                      value: String(segmentCount),
                      icon: 'bi-chat-dots',
                  },
              ]
            : [
                  {
                      key: 'subject',
                      label: 'Subject',
                      value: formData.subjectLine?.trim() ? 'Set' : 'Missing',
                      icon: 'bi-envelope',
                  },
              ]),
        {
            key: 'ready',
            label: 'Checks passed',
            value: `${readyCount}/${checks.length}`,
            icon: isReady ? 'bi-check-circle' : 'bi-exclamation-circle',
        },
    ];

    return (
        <div className="animate-fade-in ecx-complete-step">
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
                <div>
                    <h3 className="fw-normal text-dark mb-1">Ready to Launch {entityLabel}</h3>
                    <p className="text-muted mb-0">
                        Review the summary below, then launch when everything looks right.
                    </p>
                </div>
                <span
                    className={`ecx-complete-step__status ${
                        isReady ? 'is-ready' : 'is-blocked'
                    }`}
                >
                    <i className={`bi ${isReady ? 'bi-rocket-takeoff' : 'bi-hourglass-split'}`} />
                    {isReady ? 'Ready to launch' : 'Needs attention'}
                </span>
            </div>

            <div className="ecx-complete-step__stats mb-4">
                {stats.map((stat) => (
                    <div key={stat.key} className="ecx-complete-step__stat">
                        <span className="ecx-complete-step__stat-icon" aria-hidden="true">
                            <i className={`bi ${stat.icon}`} />
                        </span>
                        <div>
                            <div className="ecx-complete-step__stat-value">{stat.value}</div>
                            <div className="ecx-complete-step__stat-label">{stat.label}</div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="row g-3">
                <div className="col-lg-7">
                    <div className="ecx-complete-step__panel mb-3">
                        <div className="ecx-complete-step__panel-header">
                            <h6 className="mb-0 fw-normal">{entityLabel} details</h6>
                        </div>
                        <div className="ecx-complete-step__panel-body">
                            <dl className="ecx-complete-step__details mb-0">
                                <div className="ecx-complete-step__detail">
                                    <dt>{entityLabel} name</dt>
                                    <dd>{formData.campaignName?.trim() || 'Not set'}</dd>
                                </div>
                                <div className="ecx-complete-step__detail">
                                    <dt>Description</dt>
                                    <dd>
                                        {formData.description?.trim() || (
                                            <span className="text-muted">No description</span>
                                        )}
                                    </dd>
                                </div>
                                {additionalFields.map((field) => (
                                    <div key={field.label} className="ecx-complete-step__detail">
                                        <dt>{field.label}</dt>
                                        <dd>{field.value || 'Not set'}</dd>
                                    </div>
                                ))}
                                {isSms && (
                                    <div className="ecx-complete-step__detail">
                                        <dt>Segment guide</dt>
                                        <dd>
                                            ~{SMS_SEGMENT_LENGTH} characters per SMS segment
                                        </dd>
                                    </div>
                                )}
                            </dl>
                        </div>
                    </div>

                    <div className="ecx-complete-step__panel mb-3">
                        <div className="ecx-complete-step__panel-header">
                            <h6 className="mb-0 fw-normal">Launch checklist</h6>
                            <span className="text-muted small">
                                {readyCount} of {checks.length} complete
                            </span>
                        </div>
                        <div className="ecx-complete-step__panel-body">
                            <ul className="ecx-complete-step__checklist list-unstyled mb-0">
                                {checks.map((check) => (
                                    <li
                                        key={check.key}
                                        className={`ecx-complete-step__check ${
                                            check.ok ? 'is-ok' : 'is-missing'
                                        }`}
                                    >
                                        <span className="ecx-complete-step__check-icon" aria-hidden="true">
                                            <i
                                                className={`bi ${
                                                    check.ok ? 'bi-check-lg' : 'bi-exclamation-lg'
                                                }`}
                                            />
                                        </span>
                                        <div>
                                            <div className="fw-normal text-dark">{check.label}</div>
                                            <div className="text-muted small">{check.detail}</div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    <div className="ecx-complete-step__panel">
                        <div className="ecx-complete-step__panel-header">
                            <h6 className="mb-0 fw-normal">Selected patients</h6>
                            <span className="text-muted small">{patientCount} total</span>
                        </div>
                        <div className="ecx-complete-step__panel-body">
                            {patientCount === 0 ? (
                                <div className="ecx-page-empty py-4">
                                    <span className="ecx-page-empty__icon">
                                        <i className="bi bi-person-x" />
                                    </span>
                                    <h5 className="mb-2 text-dark">No recipients yet</h5>
                                    <p className="mb-0 text-muted">
                                        Go back to Patient List and add patients before launching.
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <div className="table-responsive">
                                        <table className="table align-middle mb-0">
                                            <thead>
                                                <tr className="text-muted small text-uppercase">
                                                    <th>Name</th>
                                                    <th>{isSms ? 'Cell Phone' : 'Email'}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {previewPatients.map((patient, index) => (
                                                    <tr key={patient.id || index}>
                                                        <td className="fw-normal">
                                                            {formatPatientName(patient)}
                                                        </td>
                                                        <td className="text-muted text-nowrap">
                                                            {isSms
                                                                ? formatPhoneDisplay(patient.phone)
                                                                : patient.email || '—'}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    {remainingPatients > 0 && (
                                        <p className="text-muted small mb-0 mt-3">
                                            +{remainingPatients} more recipient
                                            {remainingPatients === 1 ? '' : 's'} not shown
                                        </p>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>

                <div className="col-lg-5">
                    <div className="ecx-complete-step__panel ecx-complete-step__panel--preview">
                        <div className="ecx-complete-step__panel-header">
                            <h6 className="mb-0 fw-normal">
                                {isSms ? 'Message preview' : 'Email summary'}
                            </h6>
                            {isSms && <span className="badge bg-light text-muted">Sample</span>}
                        </div>
                        <div className="ecx-complete-step__panel-body">
                            {isSms ? (
                                <>
                                    <p className="text-muted fs-7 mb-4">
                                        Preview uses sample patient values
                                        {clinicInfo?.name ? ` and ${clinicInfo.name}` : ''}.
                                    </p>
                                    <div className="ecx-sms-compose__phone">
                                        <div className="ecx-sms-compose__phone-notch" />
                                        <div className="ecx-sms-compose__phone-screen">
                                            <div className="ecx-sms-compose__phone-header">
                                                <span className="ecx-sms-compose__phone-contact">
                                                    {previewValues.PATIENT_NAME}
                                                </span>
                                                <span className="ecx-sms-compose__phone-number">
                                                    Sample patient
                                                </span>
                                            </div>
                                            <div className="ecx-sms-compose__phone-body">
                                                {previewMessage ? (
                                                    <div className="ecx-sms-compose__message-bubble">
                                                        {previewMessage}
                                                    </div>
                                                ) : (
                                                    <p className="ecx-sms-compose__empty mb-0">
                                                        No message content to preview yet.
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="ecx-complete-step__email-preview">
                                    <div className="ecx-complete-step__email-row">
                                        <span className="text-muted">Subject</span>
                                        <strong>{formData.subjectLine?.trim() || 'Not set'}</strong>
                                    </div>
                                    <div className="ecx-complete-step__email-row">
                                        <span className="text-muted">Recipients</span>
                                        <strong>
                                            {patientCount} patient{patientCount === 1 ? '' : 's'}
                                        </strong>
                                    </div>
                                    <p className="text-muted small mb-0 mt-3">
                                        Email body content is prepared from your selected template and
                                        blast settings.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CompleteStep;
